import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { AdminPort } from '@/hooks/useAdminPorts';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  port: AdminPort | null;
  onSubmit: (data: Partial<AdminPort>) => Promise<void> | void;
}

const empty: Partial<AdminPort> = {
  nom: '', ville: '', code_postal: '', departement: '', region: '', pays: 'France',
  latitude: null, longitude: null, telephone: '', email: '', site_web: '',
  description: '', capacite_places: null, vhf_canal: '', services: '',
};

export const PortFormDialog = ({ open, onOpenChange, port, onSubmit }: Props) => {
  const [form, setForm] = useState<Partial<AdminPort>>(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(port ? { ...port } : empty);
  }, [open, port]);

  const set = (k: keyof AdminPort, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.nom?.trim()) return;
    setSaving(true);
    try {
      await onSubmit({
        ...form,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
        capacite_places: form.capacite_places ? Number(form.capacite_places) : null,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{port ? 'Modifier le port' : 'Ajouter un port'}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Nom du port *</Label>
            <Input value={form.nom || ''} onChange={(e) => set('nom', e.target.value)} />
          </div>
          <div>
            <Label>Ville</Label>
            <Input value={form.ville || ''} onChange={(e) => set('ville', e.target.value)} />
          </div>
          <div>
            <Label>Code postal</Label>
            <Input value={form.code_postal || ''} onChange={(e) => set('code_postal', e.target.value)} />
          </div>
          <div>
            <Label>Département</Label>
            <Input value={form.departement || ''} onChange={(e) => set('departement', e.target.value)} />
          </div>
          <div>
            <Label>Région</Label>
            <Input value={form.region || ''} onChange={(e) => set('region', e.target.value)} />
          </div>
          <div>
            <Label>Pays</Label>
            <Input value={form.pays || ''} onChange={(e) => set('pays', e.target.value)} />
          </div>
          <div>
            <Label>Canal VHF</Label>
            <Input value={form.vhf_canal || ''} onChange={(e) => set('vhf_canal', e.target.value)} />
          </div>
          <div>
            <Label>Latitude</Label>
            <Input type="number" step="0.000001" value={form.latitude ?? ''} onChange={(e) => set('latitude', e.target.value)} />
          </div>
          <div>
            <Label>Longitude</Label>
            <Input type="number" step="0.000001" value={form.longitude ?? ''} onChange={(e) => set('longitude', e.target.value)} />
          </div>
          <div>
            <Label>Téléphone</Label>
            <Input value={form.telephone || ''} onChange={(e) => set('telephone', e.target.value)} />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>Site web</Label>
            <Input value={form.site_web || ''} onChange={(e) => set('site_web', e.target.value)} />
          </div>
          <div>
            <Label>Capacité (places)</Label>
            <Input type="number" value={form.capacite_places ?? ''} onChange={(e) => set('capacite_places', e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>Services</Label>
            <Input value={form.services || ''} onChange={(e) => set('services', e.target.value)} placeholder="Carburant, eau, électricité…" />
          </div>
          <div className="md:col-span-2">
            <Label>Description</Label>
            <Textarea rows={3} value={form.description || ''} onChange={(e) => set('description', e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving || !form.nom?.trim()}>
            {saving ? 'Enregistrement…' : port ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PortFormDialog;
